import React from "react";
import { Helmet } from "react-helmet-async";
import LogisticsStory from "../components/LogisticsStory/LogisticsStory";
import ScrollLazySection from "../components/ScrollLazySection";

function SectionFallback() {
  return <div className="min-h-[120px]" aria-hidden="true" />;
}

export const MainPage = ({ language }) => {
  const isArabic = language === "ar";

  return (
    <>
      <Helmet>
        <title>
          {isArabic
            ? "Qargoquote – منصة لوجستية B2B في قطر"
            : "Qargoquote – B2B Logistics Platform in Qatar"}
        </title>

        <meta
          name="description"
          content={
            isArabic
              ? "Qargoquote منصة لوجستية B2B في قطر لطلب ومقارنة عروض أسعار الشحن البحري والجوي وإدارة الشحنات رقمياً."
              : "Qargoquote is a B2B logistics platform in Qatar for requesting and comparing sea and air freight quotes and managing shipments online."
          }
        />

        <meta
          name="keywords"
          content={
            isArabic
              ? "QargoQuote, كارغوكوت, منصة لوجستية قطر, شحن بحري قطر, شحن جوي قطر, استيراد قطر, لوجستيات قطر"
              : "QargoQuote, logistics platform Qatar, sea freight Qatar, air freight Qatar, import Qatar, B2B logistics"
          }
        />

        <link rel="canonical" href="https://qargoquote.com/" />
        <link rel="alternate" hreflang="en" href="https://qargoquote.com/" />
        <link rel="alternate" hreflang="ar" href="https://qargoquote.com/" />
        <link rel="alternate" hreflang="x-default" href="https://qargoquote.com/" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Qargoquote" />
        <meta
          property="og:title"
          content={
            isArabic
              ? "Qargoquote – منصة لوجستية B2B في قطر"
              : "Qargoquote – B2B Logistics Platform in Qatar"
          }
        />
        <meta
          property="og:description"
          content={
            isArabic
              ? "منصة لوجستية B2B في قطر لطلب ومقارنة عروض أسعار الشحن البحري والجوي وإدارة الشحنات رقمياً."
              : "Request and compare sea and air freight quotes in Qatar and manage shipments online."
          }
        />
        <meta property="og:url" content="https://qargoquote.com/" />
        <meta property="og:image" content="https://qargoquote.com/og-image.png" />
        <meta property="og:locale" content={isArabic ? "ar_QA" : "en_US"} />
        <meta property="og:locale:alternate" content={isArabic ? "en_US" : "ar_QA"} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            isArabic
              ? "Qargoquote – منصة لوجستية B2B في قطر"
              : "Qargoquote – B2B Logistics Platform in Qatar"
          }
        />
        <meta
          name="twitter:description"
          content={
            isArabic
              ? "منصة لوجستية B2B في قطر لطلب ومقارنة عروض أسعار الشحن البحري والجوي."
              : "Request and compare sea and air freight quotes in Qatar and manage shipments online."
          }
        />
        <meta name="twitter:image" content="https://qargoquote.com/og-image.png" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://qargoquote.com/#homepage",
            url: "https://qargoquote.com/",
            name: isArabic
              ? "Qargoquote – منصة لوجستية B2B في قطر"
              : "Qargoquote – B2B Logistics Platform in Qatar",
            description: isArabic
              ? "منصة لوجستية B2B في قطر لطلب ومقارنة عروض أسعار الشحن البحري والجوي وإدارة الشحنات رقمياً."
              : "Request and compare sea and air freight quotes in Qatar and manage shipments online.",
            inLanguage: isArabic ? "ar" : "en",
            isPartOf: { "@id": "https://qargoquote.com/#website" },
            about: { "@id": "https://qargoquote.com/#organization" },
          })}
        </script>
      </Helmet>

      <main>
        <LogisticsStory language={language} />

        <ScrollLazySection
          load={() =>
            import("../components/WhatIsQargoquote").then((m) => ({
              default: m.WhatIsQargoquote,
            }))
          }
          render={(WhatIsQargoquote) => <WhatIsQargoquote language={language} />}
          fallback={<SectionFallback />}
          minHeight="520px"
        />

        <ScrollLazySection
          load={() =>
            import("../components/Story").then((m) => ({ default: m.Story }))
          }
          render={(Story) => <Story language={language} />}
          fallback={<SectionFallback />}
          minHeight="640px"
        />

        <ScrollLazySection
          load={() =>
            import("../components/Faqs").then((m) => ({ default: m.Faqs }))
          }
          render={(Faqs) => <Faqs language={language} />}
          fallback={<SectionFallback />}
          minHeight="720px"
        />

        <ScrollLazySection
          load={() =>
            import("../components/GetStarted").then((m) => ({
              default: m.GetStarted,
            }))
          }
          render={(GetStarted) => (
            <GetStarted language={language} isArabic={isArabic} />
          )}
          fallback={<SectionFallback />}
          minHeight="480px"
        />
      </main>
    </>
  );
};
